"use client"

import { FC, useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  IconSettings,
  IconFilter
} from "@tabler/icons-react"
import { GanttView } from "./gantt-view"
import { BoardView } from "./board-view"
import { CalendarView } from "./calendar-view"
import { TableView } from "./table-view"
import { TimelineView } from "./timeline-view"
import { AdvancedFilter } from "../filters/advanced-filter"
import { ViewSelector, ViewType } from "../view-selector"
import { TaskPriority, TaskStatus } from "@/types/spaces"
import { useSpaceStatusTemplate } from "@/hooks/useSpaceStatusTemplate"
import { updateTaskStatus } from "@/db/space-tasks"
import { buildSpaceBoardColumns } from "@/lib/spaces/board-columns"
import {
  applyTaskFilters,
  formatTaskStatusLabel,
  groupTasksByStatus,
  normalizeTaskStatus,
  type TaskFilterCondition
} from "@/lib/spaces/task-filters"
import { StatusChangeButton } from "../tasks/status-change-button"
import { TaskDueDateBadge } from "../tasks/task-control-primitives"
import { format } from "date-fns"
import { toast } from "sonner"

interface Task {
  id: string
  title: string
  description?: string
  status: TaskStatus
  priority: TaskPriority
  due_date?: string
  start_date?: string
  assigned_to?: string
  assigned_to_name?: string
  assigned_to_avatar?: string
  time_estimate_minutes?: number
  time_spent_minutes?: number
  is_milestone?: boolean
  dependencies?: string[]
  tags?: string[]
  watchers?: string[]
  folder_id?: string
  sprint_id?: string
}

interface SpaceViewSwitcherProps {
  tasks: Task[]
  onTaskUpdate?: (task: Task) => void
  onTaskClick?: (task: Task) => void
  onCreateTask?: (status?: string) => void
  defaultView?: ViewType
  spaceId: string
  /**
   * Chat / OpenUI embed: view switcher only, no Filters/Settings/min-height,
   * flat list rows (no nested card chrome).
   */
  compact?: boolean
}

export const SpaceViewSwitcher: FC<SpaceViewSwitcherProps> = ({
  tasks,
  onTaskUpdate,
  onTaskClick,
  onCreateTask,
  defaultView = 'list',
  spaceId,
  compact = false
}) => {
  const [currentView, setCurrentView] = useState<ViewType>(defaultView)
  const [taskFilterConditions, setTaskFilterConditions] = useState<
    TaskFilterCondition[]
  >([])
  const [showFilters, setShowFilters] = useState(false)
  const { statusTemplate, availableStatuses } = useSpaceStatusTemplate(spaceId)
  const boardColumns = useMemo(
    () => buildSpaceBoardColumns({ statusTemplate }),
    [statusTemplate]
  )
  const filteredTasks = useMemo(
    () => applyTaskFilters(tasks, taskFilterConditions),
    [tasks, taskFilterConditions]
  )
  const hasActiveTaskFilters = useMemo(
    () =>
      taskFilterConditions.some(condition => {
        if (condition.operator === "is_empty" || condition.operator === "is_not_empty") {
          return true
        }

        if (Array.isArray(condition.value)) {
          return condition.value.length > 0
        }

        return !(
          condition.value === undefined ||
          condition.value === null ||
          condition.value === ""
        )
      }),
    [taskFilterConditions]
  )
  const templateOrderedStatuses = useMemo(() => {
    if (!statusTemplate?.statuses?.length) {
      return {
        orderedValues: boardColumns.map(column => column.status),
        labelsByStatus: new Map(
          boardColumns.map(column => [
            normalizeTaskStatus(column.status),
            column.title
          ])
        )
      }
    }

    const orderedStatuses = [...statusTemplate.statuses]
      .sort((left, right) => left.order - right.order)
      .map(status => ({
        value: status.statusValues?.[0] || status.id,
        label: status.name
      }))

    return {
      orderedValues: orderedStatuses.map(status => status.value),
      labelsByStatus: new Map(
        orderedStatuses.map(status => [
          normalizeTaskStatus(status.value),
          status.label
        ])
      )
    }
  }, [boardColumns, statusTemplate])
  const groupedListTasks = useMemo(
    () =>
      groupTasksByStatus(
        filteredTasks,
        templateOrderedStatuses.orderedValues,
        task => task.status,
        status =>
          templateOrderedStatuses.labelsByStatus.get(normalizeTaskStatus(status)) ||
          formatTaskStatusLabel(status)
      ).filter(group => group.tasks.length > 0),
    [filteredTasks, templateOrderedStatuses]
  )
  const handleListStatusChange = async (task: Task, status: string) => {
    try {
      await updateTaskStatus(task.id, status, spaceId)
      onTaskUpdate?.({
        ...task,
        status
      })
    } catch (error) {
      console.error("Error updating task status:", error)
      toast.error("Failed to update task status")
    }
  }

  const renderListView = () => {
    if (groupedListTasks.length === 0) {
      return (
        <div
          className={
            compact
              ? "text-muted-foreground px-1 py-6 text-center text-sm"
              : "text-muted-foreground rounded-lg border p-8 text-center"
          }
        >
          No tasks match the current filters.
        </div>
      )
    }

    return (
      <div className={compact ? "space-y-4" : "space-y-6"}>
        {groupedListTasks.map(group => (
          <section key={group.status} className="space-y-1">
            <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
              {group.label} ({group.tasks.length})
            </h3>
            <div className={compact ? "divide-y divide-border/40" : "space-y-2"}>
              {group.tasks.map(task => (
                <div
                  key={task.id}
                  className={
                    compact
                      ? "hover:bg-foreground/[0.03] flex w-full items-center justify-between gap-3 px-1 py-2.5 text-left transition-colors"
                      : "bg-card hover:bg-muted/50 flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors"
                  }
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 pr-3 text-left"
                    onClick={() => onTaskClick?.(task)}
                  >
                    <div className="truncate font-medium">{task.title}</div>
                    {task.description ? (
                      <div className="text-muted-foreground mt-0.5 line-clamp-1 text-xs">
                        {task.description}
                      </div>
                    ) : null}
                    {task.due_date ? (
                      <TaskDueDateBadge
                        iconSize={13}
                        tone="muted"
                        className="mt-1 text-xs"
                      >
                        {format(new Date(task.due_date), "MMM d, yyyy")}
                      </TaskDueDateBadge>
                    ) : null}
                  </button>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                    <StatusChangeButton
                      currentStatus={task.status}
                      onStatusChange={(status) => handleListStatusChange(task, status)}
                      size="sm"
                      showLabel={!compact}
                      availableStatuses={availableStatuses}
                      statusTemplate={statusTemplate}
                    />
                    {!compact ? (
                      <Badge variant="outline" className="capitalize">
                        {task.priority}
                      </Badge>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    )
  }

  const renderCurrentView = () => {
    const viewProps = {
      tasks: filteredTasks,
      onTaskUpdate,
      onTaskClick,
      onCreateTask,
      spaceId
    }

    switch (currentView) {
      case 'board':
        return (
          <BoardView
            {...viewProps}
            columns={boardColumns}
            isReorderEnabled={!hasActiveTaskFilters}
            statusTemplate={statusTemplate}
          />
        )
      case 'gantt':
        return <GanttView {...viewProps} />
      case 'calendar':
        return <CalendarView {...viewProps} />
      case 'table':
        return <TableView {...viewProps} />
      case 'timeline':
        return <TimelineView {...viewProps} />
      case 'list':
        return renderListView()
      default:
        return renderListView()
    }
  }

  return (
    <div className={compact ? "space-y-2" : "space-y-4"}>
      <div
        className={
          compact
            ? "flex items-center justify-between gap-2 px-1"
            : "flex items-center justify-between"
        }
      >
        <div className={compact ? "flex min-w-0 items-center gap-2" : "flex items-center gap-4"}>
          <ViewSelector
            currentView={currentView}
            onViewChange={setCurrentView}
            availableViews={
              compact
                ? ["board", "list", "table", "timeline", "calendar"]
                : ["list", "board", "timeline", "calendar", "table", "gantt"]
            }
            className="shrink-0"
          />

          {!compact ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <IconFilter size={16} className="mr-2" />
              Filters
            </Button>
          ) : null}
        </div>

        {!compact ? (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm">
              {filteredTasks.length} of {tasks.length} tasks
            </span>
            <Button variant="outline" size="sm">
              <IconSettings size={16} className="mr-2" />
              View Settings
            </Button>
          </div>
        ) : (
          <span className="text-muted-foreground shrink-0 text-[11px] tabular-nums">
            {filteredTasks.length}
          </span>
        )}
      </div>

      {!compact && showFilters ? (
        <AdvancedFilter
          spaceId={spaceId}
          onFiltersChange={setTaskFilterConditions}
          customFields={[]} // Pass actual custom fields here
        />
      ) : null}

      <div className={compact ? "min-h-0" : "min-h-96"}>
        {renderCurrentView()}
      </div>
    </div>
  )
}
